import React from "react";
import { Badge, Box, Code, Flex, Heading, Stack, Text } from "@chakra-ui/react";
import { Evidence, Issue, IssueSeverity, LocationNode, Remediation } from "./types";

const SEVERITY_COLOR: Record<IssueSeverity, string> = {
  error: "red",
  warning5: "orange",
  warning1: "yellow",
  observation: "gray",
};

const SEVERITY_LABEL: Record<IssueSeverity, string> = {
  error: "Error",
  warning5: "Needs Your Attention",
  warning1: "Conditionally Passed",
  observation: "Observation",
};

const Pointer: React.FC<{ value: string }> = ({ value }) => (
  <Code size="sm">{value || "/"}</Code>
);

const Excerpt: React.FC<{ excerpt: string }> = ({ excerpt }) => (
  <Box
    as="pre"
    fontFamily="mono"
    fontSize="sm"
    bg="bg.subtle"
    padding="6px 10px"
    borderRadius="md"
    overflowX="auto"
    whiteSpace="pre"
    margin="4px 0 0 0"
  >
    {excerpt}
  </Box>
);

const LocationNodeView: React.FC<{ node: LocationNode; tag: string }> = ({
  node,
  tag,
}) => (
  <Box>
    <Flex gap="6px" alignItems="center" wrap="wrap">
      <Badge colorPalette="blue">{tag}</Badge>
      <Badge variant="outline">{node.kind}</Badge>
      {node.label && <Badge variant="subtle">{node.label}</Badge>}
      <Pointer value={node.pointer} />
      <Text fontSize="sm" color="fg.muted">
        line {node.line}, col {node.column}
      </Text>
      {node.exists === false && (
        <Badge colorPalette="purple">missing</Badge>
      )}
    </Flex>
    {node.excerpt && <Excerpt excerpt={node.excerpt} />}
  </Box>
);

const formatJsonValue = (value: unknown): string => {
  if (value === null) return "null";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const EvidenceView: React.FC<{ evidence: Evidence }> = ({ evidence }) => {
  switch (evidence.kind) {
    case "value":
      return (
        <Stack gap="4px">
          <Text fontSize="sm" color="fg.muted">
            Offending value
          </Text>
          <Code size="sm">{formatJsonValue(evidence.value)}</Code>
        </Stack>
      );
    case "absence":
      return (
        <Stack gap="4px">
          <Text fontSize="sm" color="fg.muted">
            Expected {evidence.expectedKind}{" "}
            <Code size="sm">{evidence.expectedName}</Code> at{" "}
            <Code size="sm">{evidence.expectedAt}</Code>
          </Text>
        </Stack>
      );
    case "mismatch":
      return (
        <Stack gap="4px">
          <Text fontSize="sm">
            Expected: <Code size="sm">{formatJsonValue(evidence.expected)}</Code>
          </Text>
          <Text fontSize="sm">
            Actual: <Code size="sm">{formatJsonValue(evidence.actual)}</Code>
          </Text>
        </Stack>
      );
    case "collision":
      return (
        <Stack gap="4px">
          {evidence.scope != null && (
            <Text fontSize="sm">
              Scope: <Code size="sm">{evidence.scope}</Code>
            </Text>
          )}
          {evidence.value != null && (
            <Text fontSize="sm">
              Value: <Code size="sm">{evidence.value}</Code>
            </Text>
          )}
          <Text fontSize="sm" color="fg.muted">
            Colliding items:
          </Text>
          <Code size="sm">
            <Box
              as="pre"
              whiteSpace="pre-wrap"
              fontFamily="mono"
              fontSize="sm"
            >
              {formatJsonValue(evidence.colliding)}
            </Box>
          </Code>
        </Stack>
      );
    case "parseError":
      return (
        <Stack gap="4px">
          <Text fontSize="sm">
            Parser: <Code size="sm">{evidence.parserMessage}</Code>
          </Text>
          <Text fontSize="sm">
            At line {evidence.line}, column {evidence.column} (char {evidence.char})
          </Text>
        </Stack>
      );
    case "shapeViolation":
      return (
        <Stack gap="4px">
          <Text fontSize="sm">
            Expected: <Code size="sm">{evidence.expected}</Code>
          </Text>
          <Text fontSize="sm">
            Actual: <Code size="sm">{evidence.actual}</Code>
          </Text>
        </Stack>
      );
    case "lengthViolation":
      return (
        <Stack gap="4px">
          <Text fontSize="sm">
            Value: <Code size="sm">{formatJsonValue(evidence.value)}</Code>
          </Text>
          {evidence.minLength != null && (
            <Text fontSize="sm">Min length: {evidence.minLength}</Text>
          )}
          {evidence.maxLength != null && (
            <Text fontSize="sm">Max length: {evidence.maxLength}</Text>
          )}
        </Stack>
      );
  }
};

const RemediationView: React.FC<{ remediation: Remediation }> = ({
  remediation,
}) => (
  <Stack gap="6px">
    <Flex gap="6px" alignItems="center" wrap="wrap">
      <Badge colorPalette="teal">{remediation.action}</Badge>
      {remediation.automatic ? (
        <Badge colorPalette="green">auto-applicable</Badge>
      ) : (
        <Badge colorPalette="gray">needs human review</Badge>
      )}
    </Flex>
    <Text fontSize="sm">{remediation.message}</Text>
    {Object.keys(remediation.details).length > 0 && (
      <Box
        as="pre"
        fontFamily="mono"
        fontSize="xs"
        bg="bg.subtle"
        padding="6px 10px"
        borderRadius="md"
        overflowX="auto"
        whiteSpace="pre-wrap"
      >
        {formatJsonValue(remediation.details)}
      </Box>
    )}
  </Stack>
);

export const IssueCard: React.FC<{ issue: Issue }> = ({ issue }) => {
  const sevColor = SEVERITY_COLOR[issue.severity];
  return (
    <Box
      borderLeftWidth="4px"
      borderLeftColor={`${sevColor}.solid`}
      borderWidth="1px"
      borderRadius="md"
      padding="12px 16px"
      bg="bg.panel"
    >
      <Stack gap="10px">
        <Flex gap="8px" alignItems="center" wrap="wrap">
          <Badge colorPalette={sevColor}>{SEVERITY_LABEL[issue.severity]}</Badge>
          <Badge variant="outline">{issue.rule}</Badge>
          <Badge variant="subtle">{issue.category}</Badge>
          <Text fontSize="xs" color="fg.muted">
            {issue.id}
          </Text>
        </Flex>

        <Text fontSize="md" fontWeight="medium">
          {issue.message}
        </Text>

        <Box>
          <Heading size="xs" textTransform="uppercase" color="fg.muted">
            Location
          </Heading>
          <Stack gap="8px" marginTop="6px">
            <LocationNodeView node={issue.location.primary} tag="primary" />
            {issue.location.related.map((r, i) => (
              <LocationNodeView key={i} node={r} tag="related" />
            ))}
          </Stack>
        </Box>

        <Box>
          <Heading size="xs" textTransform="uppercase" color="fg.muted">
            Evidence ({issue.evidence.kind})
          </Heading>
          <Box marginTop="6px">
            <EvidenceView evidence={issue.evidence} />
          </Box>
        </Box>

        <Box>
          <Heading size="xs" textTransform="uppercase" color="fg.muted">
            Remediation
          </Heading>
          <Box marginTop="6px">
            <RemediationView remediation={issue.remediation} />
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};

export const IssueList: React.FC<{ issues: Issue[] }> = ({ issues }) => {
  if (issues.length === 0) {
    return <Text color="fg.muted">No issues for this selection.</Text>;
  }
  return (
    <Stack gap="12px">
      {issues.map((issue) => (
        <IssueCard key={issue.id} issue={issue} />
      ))}
    </Stack>
  );
};
